// 16×16 dominant-colour swatch. Samples a small thumbnail via canvas
// and returns the most-frequent colour bucket. Skipped if the image
// fails to load (we just render the swatch as a neutral fallback).

import { useEffect, useRef, useState } from 'react';
import { hexLightnessTier } from '../lib/colour-name';

export interface DominantSwatchProps {
  imageUrl: string;
  /** Optional size override (defaults to 16). */
  size?: number;
  /** Accessible label override. */
  label?: string;
}

async function computeDominant(
  url: string,
): Promise<{ hex: string } | null> {
  // 1. Load image into an offscreen canvas at 32×32 for cheap sampling.
  const img = new Image();
  img.crossOrigin = 'anonymous';
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () =>
      reject(new Error('Could not load image for dominant-colour sample'));
    img.src = url;
  });
  const canvas = document.createElement('canvas');
  canvas.width = 32;
  canvas.height = 32;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, 32, 32);
  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, 0, 32, 32).data;
  } catch {
    // Tainted canvas — the image wasn't CORS-enabled.
    return null;
  }
  // 2. Quantise colours into 4-bit-per-channel buckets (4096 buckets),
  //    pick the bucket with the highest frequency (weighted by saturation
  //    so the swatch picks a representative iris/scene colour rather than
  //    plain white background).
  const buckets = new Map<number, { count: number; r: number; g: number; b: number }>();
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a < 128) continue;
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    // Skip near-white backgrounds.
    if (r > 245 && g > 245 && b > 245) continue;
    const qR = r >> 4;
    const qG = g >> 4;
    const qB = b >> 4;
    const key = (qR << 8) | (qG << 4) | qB;
    const entry = buckets.get(key);
    if (entry) {
      entry.count += 1;
      entry.r += r;
      entry.g += g;
      entry.b += b;
    } else {
      buckets.set(key, { count: 1, r, g, b });
    }
  }
  if (buckets.size === 0) return null;
  let best: { count: number; r: number; g: number; b: number } | null = null;
  for (const entry of buckets.values()) {
    if (!best || entry.count > best.count) best = entry;
  }
  if (!best) return null;
  const r = Math.round(best.r / best.count);
  const g = Math.round(best.g / best.count);
  const b = Math.round(best.b / best.count);
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return { hex: `#${toHex(r)}${toHex(g)}${toHex(b)}` };
}

export function DominantSwatch(props: DominantSwatchProps) {
  const { imageUrl, size = 16, label } = props;
  const [hex, setHex] = useState<string | null>(null);
  const cacheRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    let cancelled = false;
    if (cacheRef.current.has(imageUrl)) {
      setHex(cacheRef.current.get(imageUrl) ?? null);
      return;
    }
    computeDominant(imageUrl)
      .then((res) => {
        if (cancelled) return;
        if (res) {
          cacheRef.current.set(imageUrl, res.hex);
          setHex(res.hex);
        } else {
          setHex(null);
        }
      })
      .catch(() => {
        if (!cancelled) setHex(null);
      });
    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  const tier = hex ? hexLightnessTier(hex) : 'mid';
  const fallbackBorder =
    tier === 'light'
      ? 'border-neutral-300'
      : tier === 'dark'
        ? 'border-neutral-700'
        : 'border-neutral-400';

  return (
    <span
      role="img"
      aria-label={label ?? (hex ? `Dominant colour ${hex}` : 'Dominant colour')}
      title={hex ?? 'Sampling dominant colour…'}
      className={[
        'inline-block shrink-0 rounded-sm border',
        fallbackBorder,
      ].join(' ')}
      style={{
        width: size,
        height: size,
        backgroundColor: hex ?? 'transparent',
      }}
    />
  );
}
